/**
 * HE THONG PHAN GIAI FONT - ban trinh duyet.
 *
 * Port tu tools/text-edit-prototype/src/fonts/resolve.mjs, voi mot khac biet
 * co chu dich o tang B (xem ben duoi).
 *
 * =============================================================================
 *  BAI TOAN
 * =============================================================================
 *  Trang PDF khong luu van ban, no luu lenh ve glyph. Font gan nhu luon duoc
 *  nhung dang SUBSET: chi nhung glyph da thuc su xuat hien moi co mat. Do tren
 *  284 tai lieu that: 73.9% font la subset, do phu tieng Viet cua chung co
 *  TRUNG VI 0%, va chi 11.5% du glyph de go lai tieng Viet.
 *
 *  Nghia la SUA CHU CHI BANG GLYPH CO SAN TRONG FILE LA BAT KHA THI voi da so
 *  tai lieu tieng Viet that.
 *
 * =============================================================================
 *  THU TU UU TIEN
 * =============================================================================
 *  TANG A - CHINH FONT NHUNG TRONG FILE.
 *      Tot nhat: giu nguyen hinh thuc tai lieu. Chi dung duoc khi CA BA dieu
 *      kien cung dung:
 *        - doc duoc bang sfnt/CFF,
 *        - fsType cho phep nhung de CHINH SUA (khong chi de xem/in) va khong
 *          cam tao subset,
 *        - font con du glyph cho van ban MOI.
 *      Do tren tai lieu that: fsType mot minh da chan tang A tren 74 tai lieu.
 *
 *      CANH BAO VE DO TIN CAY: byte font ma pdf.js dua ra la ban DA DUNG LAI,
 *      khong phai byte goc trong file - pdf.js co the xoa fsType ve 0. Ta danh
 *      dau fsTypeTrusted=false trong truong hop do va van THAN TRONG. Ban
 *      Rust/PDFium doc thang /FontFile2 /FontFile3 nen se chinh xac hon.
 *
 *  TANG B - FONT DONG GOI SAN (Noto Sans, OFL-1.1).
 *      KHAC BIET SO VOI BAN NODE: ban Node quet C:\Windows\Fonts. Trong trinh
 *      duyet dieu do bat kha thi, va phan phoi lai font he thong Windows la vi
 *      pham giay phep font. Tang B o day la font OFL dong goi cung ung dung -
 *      duoc phep ca nhung ca phan phoi lai.
 *      Tang nay LUON dat substituted=true: hinh thuc chu SE DOI.
 *
 *  TANG C - CHIU THUA, NOI RO LY DO bang tieng Viet.
 *
 * =============================================================================
 *  QUY TAC BAT BIEN: KHONG BAO GIO THAY THE AM THAM
 * =============================================================================
 *  Moi ket qua deu mang `tier`, `substituted` va `reason`. Khi substituted=true,
 *  hinh thuc tai lieu SE DOI va giao dien BAT BUOC phai canh bao nguoi dung.
 *  Thay font am tham chinh la cach lam hong tai lieu cua nguoi dung ma ho khong
 *  he hay biet cho toi khi in ra.
 */
import type { Font } from './fontkit';
import { fontFromBytes } from './fontkit';
import { checkCoverage, describeMissing, type NormalizationForm } from './coverage';
import {
  loadBundledFamilyFace,
  normalizeFontFamilyId,
  getFontFamily,
  DEFAULT_FONT_FAMILY_ID,
  type FontFamilyId,
  type TextFaceStyle,
} from './bundled';

export type { TextFaceStyle };

// ---------------------------------------------------------------------------
// fsType - quyen nhung font
// ---------------------------------------------------------------------------

export interface FsTypeInfo {
  fsType: number | null;
  label: string;
  installable: boolean;
  restricted: boolean;
  previewPrintOnly: boolean;
  editableFlag: boolean;
  noSubsetting: boolean;
  bitmapOnly: boolean;
  allowsEditing: boolean;
  allowsSubsetting: boolean;
  /** Font khong co bang OS/2: "khong biet", KHONG phai "duoc phep". */
  unknown: boolean;
}

/**
 * Doc GIA TRI SO THO cua fsType tu bang OS/2 (offset 8, uint16 big-endian).
 *
 * Doc tho thay vi hoi fontkit vi fontkit giai ma fsType thanh mot object co,
 * khong lo ra so goc - ma FontDiagnostics.fsType trong hop dong kieu la
 * `number | null` va bao cao can dung con so that.
 */
export function readRawFsType(bytes: Uint8Array | null | undefined): number | null {
  if (!bytes || bytes.length < 12) return null;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);

  let base = 0;
  const tag0 = view.getUint32(0, false);
  if (tag0 === 0x74746366) {
    // 'ttcf' - TrueType Collection: lay font dau tien
    if (bytes.length < 16) return null;
    base = view.getUint32(12, false);
    if (base + 12 > bytes.length) return null;
  }

  const ver = view.getUint32(base, false);
  // 0x00010000 TrueType, 'true', 'OTTO' (CFF). Khac di thi khong phai sfnt.
  if (ver !== 0x00010000 && ver !== 0x74727565 && ver !== 0x4f54544f) return null;

  const numTables = view.getUint16(base + 4, false);
  if (numTables === 0 || numTables > 512) return null;

  for (let i = 0; i < numTables; i++) {
    const rec = base + 12 + i * 16;
    if (rec + 16 > bytes.length) break;
    // Tag 'OS/2' = 0x4F532F32
    if (view.getUint32(rec, false) !== 0x4f532f32) continue;
    const off = view.getUint32(rec + 8, false);
    if (off + 10 > bytes.length) return null;
    return view.getUint16(off + 8, false);
  }
  return null; // font khong co bang OS/2 (CFF thuan, Type1 chuyen doi...)
}

/**
 * Giai nghia fsType theo dac ta OpenType.
 *
 *   bit 0 (0x0001) - du tru, phai la 0
 *   bit 1 (0x0002) - Restricted License: CAM nhung hoan toan
 *   bit 2 (0x0004) - Preview & Print only: nhung de xem/in, KHONG duoc sua
 *   bit 3 (0x0008) - Editable embedding: duoc nhung VA duoc sua
 *   bit 8 (0x0100) - No subsetting: cam tao subset
 *   bit 9 (0x0200) - Bitmap embedding only
 *   fsType == 0     - Installable Embedding: cho phep tat ca.
 *
 * Ba bit 1/2/3 le ra loai tru nhau nhung font that vi pham thuong xuyen, nen ta
 * xu ly theo DO UU TIEN NGHIEM NGAT: cam thang > chi xem/in > duoc sua.
 */
export function interpretFsType(fsType: number | null | undefined): FsTypeInfo {
  if (fsType === null || fsType === undefined) {
    return {
      fsType: null,
      label: 'không-có-bảng-OS/2',
      installable: false,
      restricted: false,
      previewPrintOnly: false,
      editableFlag: false,
      noSubsetting: false,
      bitmapOnly: false,
      // Khong co tuyen bo quyen nao. Dac ta khong noi day la "duoc phep", no
      // noi la "khong biet". Voi font nhung san ta chon THAN TRONG.
      allowsEditing: false,
      allowsSubsetting: true,
      unknown: true,
    };
  }

  const restricted = (fsType & 0x0002) !== 0;
  const previewPrint = (fsType & 0x0004) !== 0;
  const editable = (fsType & 0x0008) !== 0;
  const noSubsetting = (fsType & 0x0100) !== 0;
  const bitmapOnly = (fsType & 0x0200) !== 0;
  const installable = (fsType & 0x000f) === 0;

  let label: string;
  let allowsEditing: boolean;
  if (restricted) {
    label = 'cấm-nhúng';
    allowsEditing = false;
  } else if (installable) {
    label = 'tự-do-cài-đặt';
    allowsEditing = true;
  } else if (editable) {
    label = 'được-sửa';
    allowsEditing = true;
  } else if (previewPrint) {
    label = 'chỉ-xem-và-in';
    allowsEditing = false;
  } else {
    label = 'không-rõ';
    allowsEditing = false;
  }
  // bitmapOnly nghia la chi duoc nhung anh bitmap - khong tao duoc subset vector.
  if (bitmapOnly) allowsEditing = false;

  return {
    fsType,
    label,
    installable,
    restricted,
    previewPrintOnly: previewPrint && !editable,
    editableFlag: editable,
    noSubsetting,
    bitmapOnly,
    allowsEditing,
    allowsSubsetting: !noSubsetting,
    unknown: false,
  };
}

/** Bo tien to subset 6 chu cai: "BCDEEE+TimesNewRomanPSMT" -> "TimesNewRomanPSMT". */
export function stripSubsetPrefix(name: string | null | undefined): string {
  return typeof name === 'string' ? name.replace(/^[A-Z]{6}\+/, '') : '';
}

// ---------------------------------------------------------------------------
// Ket qua phan giai
// ---------------------------------------------------------------------------

export type FontTier = 'embedded' | 'bundled' | 'none';

export interface ResolveAttempt {
  tier: FontTier;
  ok: boolean;
  /** Ma ly do on dinh, dung de phan loai - KHONG chua noi dung tai lieu. */
  why: string;
}

export interface ResolvedFont {
  ok: boolean;
  tier: FontTier;
  /** Nhan tieng Viet cho giao dien. */
  tierLabel: string;
  /**
   * true khi hinh thuc tai lieu SE DOI. Giao dien BAT BUOC phai canh bao.
   * Day la truong quan trong nhat cua ca cau truc nay.
   */
  substituted: boolean;
  /** Ly do doc duoc, tieng Viet. Luon co, ca khi thanh cong. */
  reason: string;
  baseFont: string | null;
  font: Font | null;
  bytes: Uint8Array | null;
  fsType: number | null;
  fsTypeInfo: FsTypeInfo | null;
  /** false khi chi doc duoc fsType tu ban font pdf.js da dung lai. */
  fsTypeTrusted: boolean;
  fsTypeSource: 'byte-gốc-trong-tệp' | 'bản-pdf.js-dựng-lại' | 'font-đóng-gói' | 'không-có';
  /** Dang chuan hoa phai gui xuong font khi dat chu. */
  textForm: NormalizationForm;
  numGlyphs: number;
  unitsPerEm: number;
  /** Ma Unicode ma font nhung san thieu. Rong khi tang A dat hoac khong xet den. */
  embeddedMissing: number[];
  /**
   * HO FONT DONG GOI da duoc dung o tang B. null khi dung font nhung trong tep
   * (tang A) hoac khong phan giai duoc. Giao dien hien no de nguoi dung biet
   * phong nao that su duoc ghi ra.
   */
  fontFamily: FontFamilyId | null;
  attempts: ResolveAttempt[];
}

/** Nguon font nhung san, lay tu pdf.js hoac tu bo phan tich PDF that. */
export interface EmbeddedFontSource {
  baseFont: string | null;
  bytes: Uint8Array | null;
  isType3: boolean;
  /**
   * true khi `bytes` la byte NGUYEN BAN trong file PDF. false khi la ban pdf.js
   * dung lai - luc do fsType KHONG dang tin.
   */
  bytesAreOriginal: boolean;
}

// ---------------------------------------------------------------------------
// Diem vao chinh
// ---------------------------------------------------------------------------

/**
 * Phan giai font nguon dung duoc de tao subset moi cho `targetText`.
 *
 * @param source ban ghi font nhung san cua khoi van ban (co the null)
 * @param targetText van ban NGUOI DUNG MUON GO VAO. Bat buoc: mot font doc duoc
 *        voi fsType hop le VAN vo dung neu no thieu glyph cho chu moi.
 * @param face kieu chu nguoi dung yeu cau (dam / nghieng).
 *
 * ===========================================================================
 *  YEU CAU DAM HAY NGHIENG SE BO QUA TANG A - va do la cau tra loi dung
 * ===========================================================================
 *  Font nhung trong tep la MOT mat chu cu the. Font Regular cua tai lieu khong
 *  chua glyph dam; khong co cach nao lay ban dam cua no ra tu chinh no.
 *
 *  Hai loi ra, va ta chon loi thu hai:
 *    (a) DAM GIA: to vien glyph cho net day len. Voi tieng Viet dieu nay day
 *        dau thanh ra khoi than chu - "ế" thanh mot dong mau nhoe.
 *    (b) DOI SANG MAT CHU THAT cua ho font dong goi.
 *
 *  Loi (b) co gia phai tra ro rang: `substituted` thanh true, va giao dien BAT
 *  BUOC hien canh bao doi font. Do la dung: nguoi dung bam "Đậm" thi hinh thuc
 *  tai lieu SE doi that, va noi truoc van hon de ho phat hien luc in.
 */
export async function resolveFont(
  source: EmbeddedFontSource | null,
  targetText: string,
  face?: TextFaceStyle,
  family?: FontFamilyId | string | null,
): Promise<ResolvedFont> {
  const wantsStyled = face?.bold === true || face?.italic === true;
  // Ho font nguoi dung chon. Ma khong hop le (hoac vang) lui ve ho mac dinh -
  // xem normalizeFontFamilyId. Cua chan phu glyph o tang B se chay tren DUNG ho
  // nay, khong phai tren ho mac dinh (bai hoc so 6).
  const familyId = normalizeFontFamilyId(family ?? undefined);
  // NGUOI DUNG CHON MOT HO KHAC MAC DINH = mot y muon DOI phong ro rang, nen phai
  // BO QUA TANG A (giu font goc). Neu khong, mot khoi co font nhung con doc duoc
  // se giu nguyen font goc du nguoi dung da chon Noto Serif: tep xuat ra van la
  // font cu, con ban xem truoc lai ve Noto Serif - lua chon bi am tham vut di, va
  // ban xem truoc noi doi. (Dispatch chi mang `fontFamily` khi KHAC mac dinh, nen
  // `family` mac dinh / vang o day dung nghia "khong doi phong" -> van cho tang A.)
  const wantsSpecificFamily = familyId !== DEFAULT_FONT_FAMILY_ID;
  const attempts: ResolveAttempt[] = [];
  const result: ResolvedFont = {
    ok: false,
    tier: 'none',
    tierLabel: 'không-phân-giải-được',
    substituted: false,
    reason: '',
    baseFont: source?.baseFont ?? null,
    font: null,
    bytes: null,
    fsType: null,
    fsTypeInfo: null,
    fsTypeTrusted: false,
    fsTypeSource: 'không-có',
    textForm: 'NFC',
    numGlyphs: 0,
    unitsPerEm: 1000,
    embeddedMissing: [],
    fontFamily: null,
    attempts,
  };

  // --- TANG A: chinh font nhung trong file --------------------------------
  if (wantsStyled) {
    // Xem khoi ghi chu tren dinh nghia ham: khong co duong nao lay ban dam /
    // nghieng ra tu font Regular nhung san.
    attempts.push({ tier: 'embedded', ok: false, why: 'yêu-cầu-mặt-chữ-đậm-nghiêng' });
  } else if (wantsSpecificFamily) {
    // Nguoi dung chon mot ho font khac han: giu font goc la lam trai y ho. Xuong
    // tang B de doi that sang ho da chon (substituted = true, giao dien bao ro).
    attempts.push({ tier: 'embedded', ok: false, why: 'người-dùng-chọn-phông-khác' });
  } else if (source?.isType3) {
    // Type3 la font dinh nghia bang chinh cac lenh ve PDF. Khong co bang glyph
    // theo nghia thong thuong, khong tao subset duoc.
    attempts.push({ tier: 'embedded', ok: false, why: 'type3' });
  } else if (source?.bytes && source.bytes.length > 0) {
    const raw = readRawFsType(source.bytes);
    const info = interpretFsType(raw);
    result.fsType = raw;
    result.fsTypeInfo = info;
    result.fsTypeTrusted = source.bytesAreOriginal;
    result.fsTypeSource = source.bytesAreOriginal ? 'byte-gốc-trong-tệp' : 'bản-pdf.js-dựng-lại';

    const font = fontFromBytes(source.bytes);
    if (!font) {
      attempts.push({ tier: 'embedded', ok: false, why: 'không-đọc-được-chương-trình-font' });
    } else if (info.restricted || info.bitmapOnly) {
      attempts.push({
        tier: 'embedded',
        ok: false,
        why: `fsType-cấm: ${info.label} (0x${(raw ?? 0).toString(16)})`,
      });
    } else if (!info.allowsEditing) {
      attempts.push({
        tier: 'embedded',
        ok: false,
        why: `fsType-không-cho-sửa: ${info.label} (0x${(raw ?? 0).toString(16)})`,
      });
    } else if (!info.allowsSubsetting) {
      attempts.push({ tier: 'embedded', ok: false, why: 'fsType-cấm-tạo-subset (bit 8)' });
    } else {
      // CUA CHAN CUOI CUNG CUA TANG A: font phai DU GLYPH cho van ban moi.
      //
      // Day chinh la ly do ca he thong nay ton tai. Mot font subset doc duoc,
      // fsType hoan toan hop le, va van khong go noi mot tu tieng Viet vi
      // subset chi chua glyph da xuat hien tren trang. Bo qua buoc nay thi
      // tang A se "thanh cong" tren gan nhu moi tai lieu roi sinh ra o vuong
      // trong - dung che do hong ma toan bo bo do nay duoc dung ra de chan.
      const cov = checkCoverage(font, targetText);
      if (!cov.ok) {
        result.embeddedMissing = cov.missing;
        attempts.push({
          tier: 'embedded',
          ok: false,
          why: `thiếu-glyph: ${cov.missing.length} ký tự (${describeMissing(cov.missing, 6)})`,
        });
      } else {
        result.ok = true;
        result.tier = 'embedded';
        result.tierLabel = 'font-nhúng-trong-tệp';
        result.substituted = false;
        result.font = font;
        result.bytes = source.bytes;
        result.numGlyphs = font.numGlyphs ?? 0;
        result.unitsPerEm = font.unitsPerEm ?? 1000;
        result.textForm = cov.form ?? 'NFC';
        result.reason =
          `Dùng chính font nhúng trong tệp (${info.label}, fsType đọc từ ${result.fsTypeSource}). ` +
          'Hình thức tài liệu được giữ nguyên.';
        attempts.push({ tier: 'embedded', ok: true, why: info.label });
        return result;
      }
    }
  } else {
    attempts.push({ tier: 'embedded', ok: false, why: 'không-có-byte-font' });
  }

  // --- TANG B: font dong goi (ho da chon, OFL-1.1) -------------------------
  // Nap DUNG (ho, mat chu) da chon. Bo qua wantsStyled o day: loadBundledFamilyFace
  // tu doi cap dam/nghieng thanh mat chu, va face vang = Regular.
  const bundled = await loadBundledFamilyFace(familyId, face ?? {});
  if (!bundled) {
    attempts.push({ tier: 'bundled', ok: false, why: 'không-nạp-được-font-đóng-gói' });
  } else {
    const cov = checkCoverage(bundled.font, targetText);
    if (!cov.ok) {
      attempts.push({
        tier: 'bundled',
        ok: false,
        why: `thiếu-glyph: ${cov.missing.length} ký tự (${describeMissing(cov.missing, 6)})`,
      });
      // Font dong goi thieu glyph nghia la van ban nam ngoai pham vi Latin +
      // tieng Viet (chu Han, emoji...). Noi that ra thay vi ve o vuong.
      result.reason =
        `Văn bản mới chứa ${cov.missing.length} ký tự mà cả font trong tệp lẫn font đóng gói ` +
        `(${bundled.familyName}) đều không có glyph: ${describeMissing(cov.missing)}. ` +
        'Không thể gõ những ký tự này mà không tạo ra ô vuông trống trong tài liệu.';
      return result;
    }

    result.ok = true;
    result.tier = 'bundled';
    result.tierLabel = `font-đóng-gói-thay-thế (${getFontFamily(familyId).label})`;
    // LUON true: day la font KHAC voi font tac gia da chon.
    result.substituted = true;
    result.fontFamily = familyId;
    result.font = bundled.font;
    result.bytes = bundled.bytes;
    result.fsTypeSource = 'font-đóng-gói';
    result.fsTypeTrusted = true;
    result.numGlyphs = bundled.numGlyphs;
    result.unitsPerEm = bundled.font.unitsPerEm ?? 1000;
    result.textForm = cov.form ?? 'NFC';

    const whyEmbeddedFailed = attempts
      .filter((a) => a.tier === 'embedded' && !a.ok)
      .map((a) => a.why)
      .join('; ');
    result.reason =
      `Không dùng lại được font gốc "${stripSubsetPrefix(result.baseFont) || 'không rõ tên'}" ` +
      `(${whyEmbeddedFailed || 'không rõ lý do'}). ` +
      `Đã thay bằng font đóng gói ${bundled.familyName} (giấy phép OFL-1.1). ` +
      'HÌNH THỨC CHỮ SẼ ĐỔI — cần báo cho người dùng trước khi lưu.';
    attempts.push({ tier: 'bundled', ok: true, why: bundled.postscriptName });
    return result;
  }

  // --- TANG C: chiu thua ---------------------------------------------------
  const why = attempts.map((a) => `${a.tier}=${a.why}`).join('; ');
  result.reason =
    `Không phân giải được font nguồn cho "${stripSubsetPrefix(result.baseFont) || 'không rõ tên'}". ` +
    `Chi tiết: ${why}.`;
  return result;
}

/** Nhan ngan cho giao dien. Khong bao gio chua noi dung tai lieu. */
export function tierLabelVi(tier: FontTier): string {
  switch (tier) {
    case 'embedded':
      return 'Dùng font gốc trong tệp';
    case 'bundled':
      return 'Thay bằng font đóng gói';
    case 'none':
      return 'Không phân giải được font';
  }
}
